import { useCallback, useContext, useSyncExternalStore } from "react";
import { QueryClientContext } from "@tanstack/react-query";

import type { BootstrapMembership, BootstrapResponse } from "@/types/bootstrap";

/** The member's discount, or 0 for non-members and old backends. */
export function memberDiscountPercent(
  bootstrap: Pick<BootstrapResponse, "membership"> | null | undefined,
): number {
  const membership = bootstrap?.membership;
  if (!membership?.active) return 0;
  const percent = Number(membership.discount_percent);
  return Number.isFinite(percent) && percent > 0 && percent < 100 ? percent : 0;
}

/** Mirrors the backend charge exactly: the discount is rounded half-up, then
 *  subtracted (cart_promotions.discounted). */
export function memberPriceCents(cents: number, percent: number): number {
  if (!percent) return cents;
  return cents - Math.floor((cents * percent) / 100 + 0.5);
}

const noop = () => () => undefined;

/** Active membership + its percent. Reads the bootstrap the app shell
 *  already loads (never fetches), so any surface without it — or a render
 *  outside the query provider — prices as a non-member. */
export function useMembership(): {
  membership: BootstrapMembership | null;
  percent: number;
} {
  const client = useContext(QueryClientContext);
  const subscribe = useCallback(
    (onChange: () => void) =>
      client ? client.getQueryCache().subscribe(onChange) : noop(),
    [client],
  );
  const bootstrap = useSyncExternalStore(subscribe, () =>
    client?.getQueryData<BootstrapResponse>(["bootstrap"]),
  );
  const percent = memberDiscountPercent(bootstrap);
  return {
    membership: percent ? (bootstrap?.membership ?? null) : null,
    percent,
  };
}
